/**
 * Lines a separate sound recording (a recorder's or mixer's WAV, MP3, M4A
 * or AIFF) up with a multicam set's master, and checks it stays lined up.
 *
 * The start is found the way an angle's is (syncBySound). A recorder and a
 * camera keep time with different crystals, so over an hour the recording
 * can slide a frame or more away from the picture: the offset is measured
 * again near the end of the stretch both cover, and the difference is the
 * drift. The set stores one offset (the start's); the drift is reported so
 * the creator and the editor know the end needs a stretch.
 *
 * Recorders often run long before the cameras (a pre-show) or start late
 * (someone forgot): when the opening minutes don't match, a window is slid
 * further into each file in turn, up to a few hours in.
 */

import { ByteSource, urlByteSource } from './byteSource';
import { estimateOffset, timingForOffset } from './audioSync';
import { AngleSyncResult, SYNC_SAMPLE_RATE, SyncOptions, SyncSource, describeSyncFailure, syncAnglesBySound } from './syncBySound';
import { openMediaAudio, readTrackClip } from './mediaAudio';

/** The end check listens to this much of each file. */
export const DRIFT_WINDOW_S = 120;
/** How far the end may have slid, either way, and still be found. */
const DRIFT_SEARCH_S = 3;
/** Downloading more than this to reach the end of an MP3 or WebM isn't worth a drift check. */
const DRIFT_MAX_SCAN_BYTES = 200 * 1024 * 1024;
/** The far search: the other file's opening minutes against windows this long, this far apart. */
const FAR_ANCHOR_S = 240;
const FAR_WINDOW_S = 720;
const FAR_STEP_S = 600;
/** How far into either file the far search goes. */
export const FAR_SEARCH_LIMIT_S = 3 * 60 * 60;

export type DriftCheck =
  | {
      status: 'measured';
      /** masterTime = recordingTime + offset, at the start and near the end. */
      startOffsetS: number;
      endOffsetS: number;
      /**
       * endOffset − startOffset. Positive: by the end, the recording (placed
       * by its start offset) plays its sound ahead of the picture.
       */
      driftMs: number;
      /** Seconds of the master's timeline between the two measurements. */
      spanS: number;
      /** More than one frame. */
      noticeable: boolean;
    }
  | { status: 'skipped'; reason: 'short' | 'unclear' | 'unreadable'; detail?: string };

export interface RecordingSyncResult {
  start: AngleSyncResult;
  drift: DriftCheck | null;
}

export interface RecordingSyncOptions extends SyncOptions {
  /** The master's frame rate, for "more than a frame" (30 when unknown). */
  frameRate?: number | null;
  /** Called when the end check starts. */
  onDriftCheck?: () => void;
}

const isAbort = (error: unknown) => (error as { name?: string } | null)?.name === 'AbortError';

/**
 * Lines the recording up with the master (start offset and trim, applied
 * only for a clear match), then measures drift near the end.
 */
export const syncRecordingBySound = async (
  master: SyncSource,
  recording: SyncSource,
  options: RecordingSyncOptions = {}
): Promise<RecordingSyncResult> => {
  const { onProgress } = options;
  let [start] = await syncAnglesBySound(master, [recording], {
    ...options,
    onProgress: (progress) => onProgress?.({ ...progress, fraction: progress.fraction * 0.6 }),
  });
  // Unclear in the opening minutes: one file may have started long before the other.
  if (start.status === 'weak' || (start.status === 'failed' && /enough sound/.test(start.reason))) {
    try {
      const far = await searchFurther(master, recording, options);
      if (far) start = far;
    } catch (error) {
      if (isAbort(error)) throw error;
    }
  }
  if (start.status !== 'matched') return { start, drift: null };
  options.onDriftCheck?.();
  try {
    const drift = await measureDrift(master, recording, start.estimate.offsetS, options);
    onProgress?.({ fraction: 1, key: recording.key });
    return { start, drift };
  } catch (error) {
    if (isAbort(error)) throw error;
    return { start, drift: { status: 'skipped', reason: 'unreadable', detail: describeSyncFailure(error) } };
  }
};

const openFor = (input: SyncSource, options: SyncOptions) => {
  const openSource = options.openSource ?? ((url: string, sig?: AbortSignal) => urlByteSource(url, { signal: sig }));
  return (async () => {
    const source: ByteSource = input.source ?? (await openSource(input.url ?? '', options.signal));
    return openMediaAudio(source, { signal: options.signal });
  })();
};

/**
 * Slides a window through the recording (it started early) and then through
 * the master (the recording started late), each against the other file's
 * opening minutes, alternating, until one matches clearly. Null when none does.
 */
const searchFurther = async (
  master: SyncSource,
  recording: SyncSource,
  options: RecordingSyncOptions
): Promise<AngleSyncResult | null> => {
  const { signal, decoder, packetDecoder, onProgress } = options;
  const masterAudio = await openFor(master, options);
  const recordingAudio = await openFor(recording, options);
  const read = (audio: typeof masterAudio, from: number, to: number) =>
    readTrackClip(audio.source, audio.track, from, to, SYNC_SAMPLE_RATE, { signal, decoder, packetDecoder, maxScanBytes: DRIFT_MAX_SCAN_BYTES });
  const masterAnchor = await read(masterAudio, 0, FAR_ANCHOR_S);
  const recordingAnchor = await read(recordingAudio, 0, FAR_ANCHOR_S);
  const recordingEnd = Math.min(FAR_SEARCH_LIMIT_S, recordingAudio.track.durationSeconds);
  const masterEnd = Math.min(FAR_SEARCH_LIMIT_S, masterAudio.track.durationSeconds);
  const steps: Array<{ inRecording: boolean; from: number }> = [];
  for (let from = FAR_STEP_S; from < Math.max(recordingEnd, masterEnd) - 30; from += FAR_STEP_S) {
    if (from < recordingEnd - 30) steps.push({ inRecording: true, from });
    if (from < masterEnd - 30) steps.push({ inRecording: false, from });
  }
  for (let i = 0; i < steps.length; i += 1) {
    const { inRecording, from } = steps[i];
    onProgress?.({ fraction: 0.6 + 0.15 * ((i + 1) / steps.length), key: recording.key });
    const estimate = inRecording
      ? estimateOffset(masterAnchor, await read(recordingAudio, from, from + FAR_WINDOW_S))
      : estimateOffset(await read(masterAudio, from, from + FAR_WINDOW_S), recordingAnchor);
    if (estimate && estimate.quality !== 'weak') {
      return {
        key: recording.key,
        status: 'matched',
        estimate,
        timing: timingForOffset(estimate.offsetS, master.sourceInMs, recording.sourceInMs),
      };
    }
  }
  return null;
};

/** The offset near the end of the stretch both files cover, compared with the start's. */
export const measureDrift = async (
  master: SyncSource,
  recording: SyncSource,
  startOffsetS: number,
  options: RecordingSyncOptions = {}
): Promise<DriftCheck> => {
  const { signal, decoder, packetDecoder, onProgress } = options;
  const openSource = options.openSource ?? ((url: string, sig?: AbortSignal) => urlByteSource(url, { signal: sig }));
  const open = async (input: SyncSource) => {
    const source: ByteSource = input.source ?? (await openSource(input.url ?? '', signal));
    return openMediaAudio(source, { signal });
  };
  const masterAudio = await open(master);
  const recordingAudio = await open(recording);
  // Master time t is recording time t − offset.
  const sharedStart = Math.max(0, startOffsetS);
  const sharedEnd = Math.min(masterAudio.track.durationSeconds, recordingAudio.track.durationSeconds + startOffsetS);
  // The start was measured over the first few minutes; an end window that
  // overlaps them would measure the same thing again.
  if (!(sharedEnd - sharedStart > 2 * DRIFT_WINDOW_S + 60)) return { status: 'skipped', reason: 'short' };

  const windowEnd = sharedEnd - 1;
  const windowStart = windowEnd - DRIFT_WINDOW_S;
  const report = (base: number) => (fraction: number) =>
    onProgress?.({ fraction: 0.75 + 0.25 * (base + fraction * 0.5), key: recording.key });
  const clipOptions = { signal, decoder, packetDecoder, maxScanBytes: DRIFT_MAX_SCAN_BYTES };
  const masterClip = await readTrackClip(masterAudio.source, masterAudio.track, windowStart, windowEnd, SYNC_SAMPLE_RATE, {
    ...clipOptions,
    onProgress: report(0),
  });
  const recordingClip = await readTrackClip(
    recordingAudio.source,
    recordingAudio.track,
    Math.max(0, windowStart - startOffsetS - DRIFT_SEARCH_S),
    windowEnd - startOffsetS + DRIFT_SEARCH_S,
    SYNC_SAMPLE_RATE,
    { ...clipOptions, onProgress: report(0.5) }
  );
  const estimate = estimateOffset(masterClip, recordingClip, { minOverlapS: 30 });
  if (!estimate || estimate.quality === 'weak' || Math.abs(estimate.offsetS - startOffsetS) > DRIFT_SEARCH_S) {
    return { status: 'skipped', reason: 'unclear' };
  }
  const frameMs = 1000 / (options.frameRate && options.frameRate > 0 ? options.frameRate : 30);
  const driftMs = (estimate.offsetS - startOffsetS) * 1000;
  return {
    status: 'measured',
    startOffsetS,
    endOffsetS: estimate.offsetS,
    driftMs,
    spanS: windowEnd - sharedStart,
    noticeable: Math.abs(driftMs) > frameMs,
  };
};
